import type { ChatMessage } from "@/types";

const DEFAULT_MAX_ENTRIES = 8;
const DEFAULT_MAX_BYTES = 24 * 1024 * 1024;

function estimateMessageBytes(messages: readonly ChatMessage[]): number {
  return messages.reduce((total, message) => {
    const attachmentBytes = (message.attachments || []).reduce(
      (attachmentTotal, attachment) => attachmentTotal + String(attachment.dataUrl || "").length * 2 + String(attachment.name || "").length * 2,
      0
    );
    const sourceBytes = (message.sources || []).reduce(
      (sourceTotal, source) => sourceTotal + String(source.title || "").length * 2 + String(source.url || "").length * 2,
      0
    );
    return total + String(message.content || "").length * 2 + attachmentBytes + sourceBytes;
  }, 0);
}

/** Per-tab, byte-bounded LRU cache. Chat content is not persisted to disk. */
export class ChatMessageCache {
  private readonly entries = new Map<string, { messages: ChatMessage[]; bytes: number }>();
  private readonly maxEntries: number;
  private readonly maxBytes: number;
  private totalBytes = 0;

  constructor({ maxEntries = DEFAULT_MAX_ENTRIES, maxBytes = DEFAULT_MAX_BYTES } = {}) {
    this.maxEntries = Math.max(1, maxEntries);
    this.maxBytes = Math.max(1, maxBytes);
  }

  get(sessionId: string): ChatMessage[] | null {
    const entry = this.entries.get(sessionId);
    if (!entry) return null;
    this.entries.delete(sessionId);
    this.entries.set(sessionId, entry);
    return entry.messages;
  }

  set(sessionId: string, messages: readonly ChatMessage[]): void {
    const bytes = estimateMessageBytes(messages);
    this.delete(sessionId);
    if (bytes > this.maxBytes) return;

    this.entries.set(sessionId, { messages: [...messages], bytes });
    this.totalBytes += bytes;
    this.trim();
  }

  delete(sessionId: string): void {
    const entry = this.entries.get(sessionId);
    if (!entry) return;
    this.entries.delete(sessionId);
    this.totalBytes -= entry.bytes;
  }

  retain(sessionIds: ReadonlySet<string>): void {
    for (const sessionId of this.entries.keys()) {
      if (!sessionIds.has(sessionId)) this.delete(sessionId);
    }
  }

  private trim(): void {
    while (this.entries.size > this.maxEntries || this.totalBytes > this.maxBytes) {
      const oldestSessionId = this.entries.keys().next().value;
      if (!oldestSessionId) return;
      this.delete(oldestSessionId);
    }
  }
}
